import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeaderOne from '../../components/HeaderOne';
import Breadcrumb from '../../components/Breadcrumb';
import './AllCategoriesOne.scss';

const AllCategoriesOne = () => {
  const [hoveredId, setHoveredId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const categories = [
    {
      id: 1,
      name: "Fresh Vegetables & Fruits",
      description: "Farm fresh vegetables and seasonal fruits",
      icon: "🥬",
      count: "250+",
      totalItems: 250,
      color: "green",
      image: "/api/placeholder/300/200",
      trending: true,
      discount: "Up to 30% OFF"
    },
    {
      id: 2,
      name: "Beverages",
      description: "Refreshing drinks and healthy juices",
      icon: "🥤",
      count: "180+",
      totalItems: 180,
      color: "blue",
      image: "/api/placeholder/300/200",
      trending: false,
      discount: "Buy 2 Get 1 Free"
    },
    {
      id: 3,
      name: "Meats & Seafood",
      description: "Fresh cuts and premium seafood",
      icon: "🐟",
      count: "120+",
      totalItems: 120,
      color: "red",
      image: "/api/placeholder/300/200",
      trending: true,
      discount: "Fresh Today"
    },
    {
      id: 4,
      name: "Breakfast & Dairy",
      description: "Start your day right with fresh dairy",
      icon: "☕",
      count: "200+",
      totalItems: 200,
      color: "yellow",
      image: "/api/placeholder/300/200",
      trending: false,
      discount: "Best Sellers"
    },
    {
      id: 5,
      name: "Frozen Foods",
      description: "Quick meals and frozen essentials",
      icon: "❄️",
      count: "95+",
      totalItems: 95,
      color: "cyan",
      image: "/api/placeholder/300/200",
      trending: false,
      discount: "Ready to Cook"
    },
    {
      id: 6,
      name: "Biscuits & Snacks",
      description: "Crunchy snacks and sweet treats",
      icon: "🍪",
      count: "150+",
      totalItems: 150,
      color: "orange",
      image: "/api/placeholder/300/200",
      trending: true,
      discount: "Family Packs"
    },
    {
      id: 7,
      name: "Grocery & Staples",
      description: "Kitchen essentials and daily needs",
      icon: "🛒",
      count: "300+",
      totalItems: 300,
      color: "purple",
      image: "/api/placeholder/300/200",
      trending: false,
      discount: "Bulk Orders"
    },
    {
      id: 8,
      name: "Personal Care",
      description: "Beauty and wellness products",
      icon: "🧴",
      count: "180+",
      totalItems: 180,
      color: "pink",
      image: "/api/placeholder/300/200",
      trending: true,
      discount: "New Arrivals"
    }
  ];

  const renderCategoryCard = (category) => (
    <div
      key={category.id}
      className={`category-card category-card--${category.color} ${
        hoveredId === category.id ? 'category-card--hovered' : ''
      }`}
      onMouseEnter={() => setHoveredId(category.id)}
      onMouseLeave={() => setHoveredId(null)}
    >
      {category.trending && (
        <div className="category-card__badge">
          <span>Trending</span>
        </div>
      )}

      <div className="category-card__image">
        <div className="category-card__icon">
          <span>{category.icon}</span>
        </div>
        <div className="category-card__overlay">
          <Link to="/product-list-one" className="category-card__quick-view">
            Quick View
          </Link>
        </div>
      </div>

      <div className="category-card__content">
        <h3 className="category-card__title">{category.name}</h3>
        <p className="category-card__description">{category.description}</p>
        
        <div className="category-card__stats">
          <span className="category-card__count">{category.count} items</span>
          <span className="category-card__discount">{category.discount}</span>
        </div>

        <div className="category-card__actions">
          <Link to="/product-list-one" className="category-card__btn category-card__btn--primary">
            Shop Now
          </Link>
          <button className="category-card__btn category-card__btn--secondary">
            <span>♡</span>
          </button>
        </div>
      </div>

      <div className="category-card__arrow">
        <span>→</span>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="categories-loading">
        <div className="loading-spinner"></div>
        <p>Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="clean-categories">
      <HeaderOne />

      {/* Hero Section */}
      <section className="categories-hero">
        <div className="categories-hero__container">
          <div className="categories-hero__content">
            <h1 className="categories-hero__title">
              Shop by Category
              <span className="categories-hero__accent">✨</span>
            </h1>
            <p className="categories-hero__subtitle">
              Discover thousands of products across all categories
            </p>
            <div className="categories-hero__stats">
              <div className="stat">
                <strong>1000+</strong>
                <span>Products</span>
              </div>
              <div className="stat">
                <strong>8</strong>
                <span>Categories</span>
              </div>
              <div className="stat">
                <strong>24/7</strong>
                <span>Delivery</span>
              </div>
            </div>
          </div>
        </div>
        <div className="categories-hero__bg"></div>
      </section>

      <Breadcrumb title={"Shop"} />

      {/* Main Content */}
      <main className="categories-main">
        <div className="categories-container">
          {/* Header Controls */}
          <div className="categories-header">
            <div className="categories-header__left">
              <h2 className="categories-header__title">All Categories</h2>
              <p className="categories-header__subtitle">
                {categories.length} categories • {categories.reduce((sum, cat) => sum + cat.totalItems, 0)} total items
              </p>
            </div>
            
            <div className="categories-header__right">
              <div className="view-toggle">
                <button 
                  className={`view-toggle__btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid view"
                >
                  ⊞
                </button>
                <button 
                  className={`view-toggle__btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="List view"
                >
                  ☰
                </button>
              </div>
            </div>
          </div>

          {/* Categories Grid */}
          <div className={`categories-grid ${viewMode === 'list' ? 'categories-grid--list' : ''}`}>
            {categories.map(renderCategoryCard)}
          </div>

          {/* Call to Action */}
          <div className="categories-cta">
            <div className="categories-cta__content">
              <h3>Can't find what you're looking for?</h3>
              <p>Browse our complete product catalog or use our search feature</p>
              <div className="categories-cta__actions">
                <Link to="/products" className="cta-btn cta-btn--primary">
                  View All Products
                </Link>
                <Link to="/search" className="cta-btn cta-btn--secondary">
                  Advanced Search
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AllCategoriesOne;